import { sanitizeAs400Text, type PricelistTable } from '@erp/rpc';
import {
  applyPriceColumnNumber,
  detectPriceColumnLocales,
  type PriceDecimalSeparator,
} from '@erp/shared-utils';

/** One grid row: cell text keyed by the table's header. */
export type CsvGridRow = Record<string, string>;

/**
 * The editable copy of one extracted table. `rows` is the array Handsontable
 * owns and mutates in place while the user types; `originalRows` is the
 * untouched server copy, kept to tell an edit from a revert.
 */
export interface CsvTableDraft {
  headers: string[];
  rows: CsvGridRow[];
  originalRows: CsvGridRow[];
  /** The table's name in the file it came from — a tab name, or null. */
  name: string | null;
  priceLocales: ReadonlyArray<PriceDecimalSeparator | null | undefined>;
}

/** Identifies one cell across all of a pricelist's tables. */
export function csvCellKey(
  tableIndex: number,
  rowIndex: number,
  header: string,
): string {
  return `${tableIndex}:${rowIndex}:${header}`;
}

/** Every cell filled in, so Handsontable edits write to a present key. */
function toGridRow(headers: string[], row: CsvGridRow): CsvGridRow {
  const filled: CsvGridRow = {};
  for (const header of headers) {
    filled[header] = row[header] ?? '';
  }
  return filled;
}

function rowMatrix(
  headers: readonly string[],
  rows: readonly CsvGridRow[],
): string[][] {
  return rows.map((row) => headers.map((header) => row[header] ?? ''));
}

/** Fresh editable drafts of the fetched tables, one per table. */
export function buildCsvDrafts(tables: PricelistTable[]): CsvTableDraft[] {
  return tables.map((table) => {
    const rows = table.rows.map((row) => toGridRow(table.headers, row));
    const originalRows = table.rows.map((row) => toGridRow(table.headers, row));
    return {
      headers: table.headers,
      rows,
      originalRows,
      name: table.name ?? null,
      priceLocales: detectPriceColumnLocales(
        table.headers,
        rowMatrix(table.headers, originalRows),
      ),
    };
  });
}

export function applyDraftPriceChange(
  draft: {
    headers: readonly string[];
    priceLocales: CsvTableDraft['priceLocales'];
  },
  header: string,
  value: string,
): string {
  const index = draft.headers.indexOf(header);
  const locale = index >= 0 ? draft.priceLocales[index] : undefined;
  const priced =
    locale === undefined ? value : applyPriceColumnNumber(value, locale);
  return sanitizeAs400Text(priced);
}

/**
 * The drafts as the contract's table shape, for saving.
 *
 * Names go back with the rows: a save rewrites the whole stored CSV, so a
 * table sent without the name it came with would be a table renamed to
 * nothing by someone correcting a price.
 */
export function draftsToTables(drafts: CsvTableDraft[]): PricelistTable[] {
  return drafts.map((draft) => ({
    headers: draft.headers,
    rows: draft.rows.map((row) =>
      sanitizeCsvGridRow(
        draft.headers,
        toGridRow(draft.headers, row),
        draft.priceLocales,
      ),
    ),
    name: draft.name,
  }));
}

/**
 * Force extracted price-file cells to the AS/400 spelling as they are saved.
 * Same policy as extraction ({@link sanitizeAs400Text}); headers stay as the
 * vendor named them.
 */
export function sanitizeCsvGridRow(
  headers: readonly string[],
  row: CsvGridRow,
  priceLocales?: readonly (PriceDecimalSeparator | null | undefined)[],
): CsvGridRow {
  const sanitized: CsvGridRow = {};
  headers.forEach((header) => {
    sanitized[header] = applyDraftPriceChange(
      { headers, priceLocales: priceLocales ?? [] },
      header,
      row[header] ?? '',
    );
  });
  return sanitized;
}

/** The value the server last gave us for a cell, or '' when out of range. */
export function originalCellValue(
  draft: CsvTableDraft | undefined,
  rowIndex: number,
  header: string,
): string {
  return draft?.originalRows[rowIndex]?.[header] ?? '';
}

/**
 * Fold a batch of changed cells into the edited-cell set. A cell typed back to
 * its original value drops out, so reverting every change clears the unsaved
 * state. Batched (one set copy per change event) to keep large pastes cheap.
 */
export function applyEditedCells(
  edited: ReadonlySet<string>,
  edits: Array<{ key: string; value: string; originalValue: string }>,
): Set<string> {
  const next = new Set(edited);
  for (const { key, value, originalValue } of edits) {
    if (value === originalValue) next.delete(key);
    else next.add(key);
  }
  return next;
}
